import "handsontable/dist/handsontable.full.css";

import {
  Button,
  FormControl,
  MenuItem,
  TablePagination,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import FormError, { useError } from "../components/FormError";

import ConfirmationDialog from "../components/ConfirmationDialog";
import Handsontable from "handsontable";
import { HotTable } from "@handsontable/react";
import React from "react";
import _ from "lodash";
import api from "../api";
import dayjs from "dayjs";
import { localStorageCache } from "../lithic/localStorageHelper";
import usStateCodes from "../modules/usStateCodes";
import { useTheme } from "@material-ui/core/styles";
import useToggle from "../state/useToggle";

export default function AffiliatedListPage() {
  const tableRef = React.useRef();
  const changeToggle = useToggle();
  const addToggle = useToggle();
  const theme = useTheme();
  const classes = useStyles();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [search, setSearch] = React.useState("");
  const [data, setData] = React.useState(null);
  const [changes, setChanges] = React.useState(null);

  const [debouncedSearch, setDebouncedSearch] = React.useState(search);

  const debouncedSearchHandler = React.useCallback(
    _.debounce((value) => {
      setDebouncedSearch(value);
    }, 300),
    []
  );

  const [formMessage, setFormMessage] = useError();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  function saveAttribute({ id, param, newValue }) {
    api
      .updateAffiliate({ id, [param]: newValue })
      .then(api.pickData)
      .then(changeToggle.turnOff)
      .catch((error) => {
        hotInstance.undo();
        setFormMessage(error);
      });
  }

  function addAffiliate(affiliate) {
    setFormMessage("");
    return api
      .createAffiliate(affiliate)
      .then(api.getAffiliates)
      .then((resp) => {
        return (
          setData({ items: resp.data.items, totalCount: resp.data.totalCount }),
          addToggle.turnOff()
        );
      })
      .catch((error) => {
        let errorMsg;
        if (_.get(error, "response.status") === 400) {
          const msg = _.get(error, "response.data.error.message");
          if (msg === "Payout_email is already taken")
            errorMsg =
              "It looks like affiliate with this PayPal email already exists";
        }
        setFormMessage(errorMsg || error);
      });
  }

  const hotInstance = tableRef.current?.hotInstance;

  React.useEffect(() => {
    api
      .getAffiliates({
        page: page + 1,
        search: debouncedSearch,
        perPage: rowsPerPage
      })
      .then((resp) => {
        setData({ items: resp.data.items, totalCount: resp.data.totalCount });
      });
  }, [page, debouncedSearch, rowsPerPage]);

  function firstRowRenderer(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  ) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.style.color = theme.palette.primary.contrastText;
    td.style.background = theme.palette.primary.main;
    td.style.fontSize = theme.typography.fontSizeTable;
  }

  function statusRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    if (value === "active") td.style.background = theme.palette.success.main;
    else if (value === "submitted")
      td.style.background = theme.palette.warning.main;
    else td.style.background = theme.palette.info.main;

    td.style.fontSize = theme.typography.fontSizeTable;
    td.style.color = theme.palette.primary.contrastText;
  }

  function cellCustomRenderer(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  ) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.style.fontSize = theme.typography.fontSizeTable;
  }

  function affiliateLinkRenderer(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  ) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.innerHTML = value ? `<a href="/affiliates/${value}">${value}</a>` : "";
    td.style.fontSize = theme.typography.fontSizeTable;
    return td;
  }

  function linkRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.innerHTML = value ? `<a href=${value}>${value}</a>` : "";
    td.style.fontSize = theme.typography.fontSizeTable;
    return td;
  }

  function dateRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.innerHTML = value ? dayjs(value).format("MMM DD, YYYY") : "";
    td.style.fontSize = theme.typography.fontSizeTable;
    return td;
  }

  const tableSetttings = {
    data: data?.items,
    className: "htCenter htMiddle handsontable",
    rowHeaders: true,
    columns: [
      {
        data: "id",
        title: "Id",
        renderer: affiliateLinkRenderer
      },
      {
        data: "createdAt",
        title: "Created",
        renderer: dateRenderer
      },
      {
        data: "status",
        title: "Status",
        renderer: statusRenderer
      },

      {
        data: "legalEntity.contactFirstName",
        title: "First Name"
      },
      {
        data: "legalEntity.contactLastName",
        title: "Last Name"
      },
      {
        data: "affiliateCode",
        title: "Affiliate Code",
        renderer: firstRowRenderer
      },

      {
        data: "legalEntity.phone",
        title: "Phone Number"
      },

      {
        data: "legalEntity.companyName",
        title: "Company Name",
        width: 200
      },
      {
        data: "legalEntity.email",
        title: "Company Email"
      },
      {
        title: "Company Website",
        data: "companyUrl",
        renderer: linkRenderer
      },
      {
        data: "legalEntity.type",
        title: "Business Type"
      },
      {
        data: "referrerAffiliateCode",
        title: "Referrer Affiliate Code"
      },
      {
        data: (affiliate) => affiliate.legalEntity?.address?.city,
        title: "City",
        readOnly: true
      },
      {
        data: (affiliate) => affiliate.legalEntity?.address?.address1,
        title: "Address Line 1",
        readOnly: true
      },
      {
        data: (affiliate) => affiliate.legalEntity?.address?.address2,
        title: "Address Line 2",
        width: 120,
        readOnly: true
      },
      {
        data: (affiliate) => affiliate.legalEntity?.address?.stateOrProvince,
        title: "State",
        readOnly: true
      },
      {
        data: (affiliate) => affiliate.legalEntity?.address?.postalCode,
        title: "Zip Code",
        readOnly: true
      }
    ],
    afterGetColHeader: (column, TH) => {
      TH.style.fontWeight = "bold";
      TH.style.fontSize = theme.typography.fontSizeTable;
    },
    height: "75vh",
    renderer: cellCustomRenderer,
    width: "100%",
    rowHeights: 35,
    dropdownMenu: true,
    filters: true,
    search: true,
    columnSorting: true,
    fixedColumnsLeft: 2,
    overflow: "hidden",
    currentRowClassName: classes.activeRow,
    licenseKey: "non-commercial-and-evaluation",
    afterLoadData: (sourceData, initialLoad, source) => {
      let conditions = localStorageCache.getItem("filterConditions", []);
      if (initialLoad) localStorageCache.removeItem("filterConditions");
      else if (conditions.length) {
        let plugin = hotInstance.getPlugin("Filters");
        conditions.forEach((condition) => {
          plugin.addCondition(
            condition.column,
            condition.conditions[0].name,
            condition.conditions[0].args,
            condition.operation
          );
        });
        plugin.filter();
      }
    },
    afterFilter: (conditionsStack) => {
      localStorageCache.setItem("filterConditions", conditionsStack);
    },
    afterSetDataAtCell: (changes, source) => {
      changes?.forEach(([row, prop, oldValue, newValue]) => {
        const currentRow = hotInstance.toPhysicalRow(row);
        const rowData = hotInstance.getSourceDataAtRow(currentRow);
        const id = rowData.id;
        const param = prop.includes(".") ? prop.split(".").pop() : prop;
        if (oldValue !== newValue && source === "edit") {
          setFormMessage();
          changeToggle.turnOn();
          setChanges({ id, param, newValue });
        }
      });
    }
  };

  return (
    <>
      <ConfirmationDialog
        open={changeToggle.on}
        onCancel={() => {
          hotInstance.undo();
          if (changeToggle.on) changeToggle.turnOff();
        }}
        onConfirm={() => {
          if (changeToggle.on) saveAttribute(changes);
        }}
        title="Are you sure you want to save changes?"
        cancelProps={{ children: "Go back" }}
        confirmProps={{ children: "Yes", disabled: formMessage }}
      >
        <FormError error={formMessage} resetError={setFormMessage} />
      </ConfirmationDialog>
      <div className={classes.header}>
        <Typography variant="h5" color="primary">
          Affiliate Partners
        </Typography>
        <TextField
          label="Search"
          variant="outlined"
          onChange={(event) => {
            setSearch(event.target.value);
            debouncedSearchHandler(event.target.value);
          }}
        />
        <div>
          <Button
            color="primary"
            variant="contained"
            onClick={addToggle.turnOn}
          >
            Add new affiliate
          </Button>
        </div>
      </div>
      <AffiliateForm
        toggle={addToggle}
        addAffiliate={addAffiliate}
        error={formMessage}
        resetError={setFormMessage}
      />
      <HotTable settings={tableSetttings} ref={tableRef} />
      <TablePagination
        component="div"
        count={data?.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25]}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  activeRow: {
    background: theme.palette.primary.main
  },

  header: {
    margin: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  }
}));

function AffiliateForm({ toggle, addAffiliate, error, resetError }) {
  const [affiliateCode, setAffiliateCode] = React.useState("");
  const [contactFirstName, setContactFirstName] = React.useState("");
  const [contactLastName, setContactLastName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [companyUrl, setCompanyUrl] = React.useState("");
  const [type, setType] = React.useState("");
  const [referrerAffiliateCode, setReferrerAffiliateCode] = React.useState("");
  const [city, setCity] = React.useState("");
  const [address1, setAddress1] = React.useState("");
  const [address2, setAddress2] = React.useState("");
  const [stateOrProvince, setStateOrProvince] = React.useState("");
  const [postalCode, setPostalCode] = React.useState("");

  const newAffiliate = {
    affiliateCode,
    contactFirstName,
    contactLastName,
    phone,
    companyName,
    email,
    companyUrl,
    type,
    referrerAffiliateCode,
    city,
    address1,
    address2,
    stateOrProvince,
    postalCode
  };

  const valid = affiliateCode && phone && email;

  return (
    <ConfirmationDialog
      title="Add new affiliate"
      open={toggle.on}
      onCancel={toggle.turnOff}
      onConfirm={() => {
        addAffiliate(newAffiliate);
      }}
      cancelProps={{ children: "Go back" }}
      confirmProps={{ children: "Yes", disabled: !valid }}
    >
      <FormError error={error} resetError={resetError} />
      <FormControl margin="normal" fullWidth>
        <TextField
          required
          label="Affiliate code"
          type="text"
          value={affiliateCode}
          variant="outlined"
          onChange={(e) => setAffiliateCode(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="First Name"
          type="text"
          value={contactFirstName}
          variant="outlined"
          onChange={(e) => setContactFirstName(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Last Name"
          type="text"
          value={contactLastName}
          variant="outlined"
          onChange={(e) => setContactLastName(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          required
          label="Phone Number"
          type="number"
          value={phone}
          variant="outlined"
          onChange={(e) => setPhone(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Company Name"
          type="text"
          value={companyName}
          variant="outlined"
          onChange={(e) => setCompanyName(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          required
          label="Company Email"
          type="email"
          value={email}
          variant="outlined"
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Company Website"
          type="email"
          value={companyUrl}
          variant="outlined"
          onChange={(e) => setCompanyUrl(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Business Type"
          type="text"
          value={type}
          variant="outlined"
          onChange={(e) => setType(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Referrer Affiliate Code"
          type="text"
          value={referrerAffiliateCode}
          variant="outlined"
          onChange={(e) => setReferrerAffiliateCode(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="City"
          type="text"
          value={city}
          variant="outlined"
          onChange={(e) => setCity(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Adress Line 1"
          type="text"
          value={address1}
          variant="outlined"
          onChange={(e) => setAddress1(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Adress Line 2"
          type="text"
          value={address2}
          variant="outlined"
          onChange={(e) => setAddress2(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="State"
          type="text"
          value={stateOrProvince}
          variant="outlined"
          onChange={(e) => setStateOrProvince(e.target.value)}
          select
        >
          {usStateCodes.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Zip Code"
          type="text"
          value={postalCode}
          variant="outlined"
          onChange={(e) => setPostalCode(e.target.value)}
        />
      </FormControl>
    </ConfirmationDialog>
  );
}
